import type { InstallmentEntry } from '../components/OpportunityPageTypes';

export function productLineTotal(
  price: number,
  quantity: number,
  discount: number,
): number {
  return price * quantity * (1 - (discount || 0) / 100);
}

/** Split total into equal installment amounts; remainder goes on the last slice. */
export function equalInstallmentAmounts(total: number, count: number): number[] {
  if (count <= 0) return [];
  const roundedTotal = Math.round(total * 100) / 100;
  const base = Math.floor((roundedTotal / count) * 100) / 100;
  const amounts = Array.from({ length: count }, () => base);
  const assigned = base * (count - 1);
  amounts[count - 1] = Math.round((roundedTotal - assigned) * 100) / 100;
  return amounts;
}

export function buildInstallmentEntries(
  count: number,
  total: number,
  previous: InstallmentEntry[] = [],
  defaultClientId = '',
): InstallmentEntry[] {
  const amounts = equalInstallmentAmounts(total, count);
  return Array.from({ length: count }, (_, i) => ({
    data: previous[i]?.data ?? null,
    client_id: previous[i]?.client_id || defaultClientId,
    amount: amounts[i],
  }));
}

/** Recalculate installment amounts below editedIndex after a non-last edit. */
export function recalculateInstallmentsBelow(
  installments: InstallmentEntry[],
  editedIndex: number,
  total: number,
): InstallmentEntry[] {
  if (editedIndex >= installments.length - 1) {
    return installments;
  }

  const updated = installments.map((entry) => ({ ...entry }));
  const sumThroughEdited = updated
    .slice(0, editedIndex + 1)
    .reduce((sum, entry) => sum + (entry.amount ?? 0), 0);
  const remainingCount = updated.length - editedIndex - 1;
  const remainder = Math.max(0, Math.round((total - sumThroughEdited) * 100) / 100);
  const belowAmounts = equalInstallmentAmounts(remainder, remainingCount);

  for (let i = 0; i < remainingCount; i++) {
    updated[editedIndex + 1 + i] = {
      ...updated[editedIndex + 1 + i],
      amount: belowAmounts[i],
    };
  }

  return updated;
}

export function installmentsSum(installments: InstallmentEntry[]): number {
  return installments.reduce((sum, entry) => sum + (entry.amount ?? 0), 0);
}

export function installmentsAmountsValid(
  installments: InstallmentEntry[],
  total: number,
  tolerance = 0.01,
): boolean {
  if (installments.length === 0) return false;
  if (!installments.every((entry) => (entry.amount ?? 0) > 0)) return false;
  return Math.abs(installmentsSum(installments) - total) <= tolerance;
}
